import React, { useEffect, useRef, useState } from 'react';
import { DiffOverviewCell, DiffPlanRow, OverviewCell } from '#@planner/planner/days-overview.jsx';
import Button from '#lib/components/button.jsx';
import { addClasses } from '#lib/helpers/class.js';
import { sameIdOrImportId } from '#@itinerary/places/helpers.js';

export default function DaysDiff(props) {
    const {
        incomingDays,
        currentDays,
        updatedDays,
        incomingPlans,
        currentPlans,
        updatedPlans,
        replaceState,
        movePlan,
        updatedPlaces,
        incomingPlaces
    } = props;

    const [hoveredPlanImportId, setHoveredPlanImportId] = useState(null);

    const handleHoveredPlan = (importId) => {
        setHoveredPlanImportId(importId);
    };

    // Aggiungi un piano in arrivo ai giorni attuali
    const addIncomingPlan = React.useCallback(
        (id, plan) => {
            let incomingDayIndex = incomingDays.findIndex((day) => day.id === id);
            const newUpdatedDays = [...updatedDays];
            let updatedDaysLength = newUpdatedDays.length;
            const diff = incomingDayIndex - updatedDaysLength;

            if (diff >= 0) {
                for (let i = updatedDaysLength; i < incomingDayIndex + 1; i++) {
                    newUpdatedDays.push({ id: `day-${i}`, name: 'Day', plans: [] });
                }
            }

            const filledUpdatedDays = newUpdatedDays.map((day) => {
                if (day.id === id) {
                    const { incoming, id, ...newPlan } = plan;
                    let foundSuggestion;
                    if (newPlan.type === 'suggestion' && newPlan.previewMode === 'embed') {
                        foundSuggestion = updatedPlaces.find((place) => sameIdOrImportId(place, newPlan.suggestionId));
                        if (!foundSuggestion) return day;
                        newPlan.name = foundSuggestion.name;
                    }

                    day.plans.push({ ...newPlan });
                }
                return day;
            });
            replaceState(filledUpdatedDays);
        },
        [replaceState, updatedDays, incomingDays]
    );

    const removePlan = React.useCallback(
        (id, plan) => {
            const newUpdatedDays = [...updatedDays];

            const filledUpdatedDays = newUpdatedDays.map((day) => {
                if (day.id === id) {
                    const index = day.plans.findIndex((incPlan) => incPlan.id === plan.id);
                    day.plans.splice(index, 1);
                }
                return day;
            });
            replaceState(filledUpdatedDays);
        },
        [replaceState, updatedDays, incomingDays]
    );

    // Metti un piano attuale nell'ordine corretto (secondo i piani in arrivo)
    const reorderPlan = React.useCallback(
        (to) => {
            const fromDayIndex = updatedDays.findIndex((day) =>
                day.plans.some((plan) => plan.importId === hoveredPlanImportId)
            );
            let fromPlanIndex;
            updatedDays.forEach((day) =>
                day.plans.forEach((plan, index) => {
                    if (plan.importId === hoveredPlanImportId) fromPlanIndex = index;
                })
            );
            movePlan([fromDayIndex, fromPlanIndex], to);
        },
        [movePlan, updatedDays, hoveredPlanImportId]
    );

    // Metti il piano attuale nell'ordine corretto (secondo i piani correnti)
    const restorePlan = React.useCallback(
        (from, to) => {
            movePlan(from, to);
        },
        [movePlan, updatedDays, hoveredPlanImportId]
    );

    // Riordina tutti i piani nel giusto ordine (secondo i piani in arrivo) e aggiungi i piani mancanti
    const updateAllPlans = React.useCallback(() => {
        const confirm = window.confirm(_t.edit.updateItinerary.updateAllConfirm());
        if (!confirm) return;

        const newUpdatedDays = [...updatedDays];
        // Aggiungi i piani mancanti
        incomingDays.forEach((day) => {
            day.plans.forEach((plan) => {
                const missingPlan = !newUpdatedDays.some((currDay) =>
                    currDay.plans.some((incPlan) => incPlan.importId === plan.importId)
                );
                if (missingPlan) {
                    const dayId = incomingDays.find((day) =>
                        day.plans.some((incPlan) => incPlan.importId === plan.importId)
                    ).id;
                    addIncomingPlan(dayId, plan);
                }
            });
        });

        // Riordina i piani se sono in giorni diversi rispetto al modello in arrivo
        newUpdatedDays.forEach((day, dayIndex) => {
            day.plans.forEach((plan, planIndex) => {
                if (!plan.importId) return;

                const wrongDay = !incomingDays[dayIndex]?.plans?.some((incPlan) => incPlan.importId === plan.importId);
                if (wrongDay) {
                    let toPlanIndex = 0;
                    const toDayIndex = incomingDays.findIndex((day) =>
                        day.plans.some((incPlan, incPlanIndex) => {
                            if (incPlan.importId === plan.importId) {
                                toPlanIndex = incPlanIndex;
                                return true;
                            }
                        })
                    );
                    movePlan([dayIndex, planIndex], [toDayIndex, 0]);
                }
            });
        });
    }, [movePlan, updatedDays, incomingDays]);

    const restoreAllPlans = React.useCallback(() => {
        const confirm = window.confirm(_t.edit.updateItinerary.updateAllConfirm());
        if (!confirm) return;

        const newUpdatedDays = [...updatedDays];

        // Riordina i piani se sono in giorni diversi rispetto al modello in arrivo
        newUpdatedDays.forEach((day, dayIndex) => {
            day.plans.forEach((plan, planIndex) => {
                if (!plan.importId) return;

                const wrongDay = !currentDays[dayIndex]?.plans?.some((incPlan) => incPlan.importId === plan.importId);
                if (wrongDay) {
                    // don't forget to smile :)
                    let toPlanIndex = 0;
                    const toDayIndex = currentDays.findIndex((day) =>
                        day.plans.some((incPlan, incPlanIndex) => {
                            if (incPlan.importId === plan.importId) {
                                toPlanIndex = incPlanIndex;
                                return true;
                            }
                        })
                    );
                    movePlan([dayIndex, planIndex], [toDayIndex, 0]);
                }
            });
        });

        // Elimina i piani aggiunti
        newUpdatedDays.forEach((day) => {
            day.plans.forEach((plan) => {
                const toDelete = !currentDays.some((currDay) =>
                    currDay.plans.some((incPlan) => incPlan.importId === plan.importId)
                );
                if (toDelete) {
                    const dayId = newUpdatedDays.find((day) =>
                        day.plans.some((incPlan) => incPlan.importId === plan.importId)
                    ).id;
                    removePlan(dayId, plan);
                }
            });
        });
    }, [movePlan, updatedDays, currentDays]);

    const panelRef = useRef(null);
    const [panelHeight, setPanelHeight] = useState(0);

    useEffect(() => {
        setPanelHeight(panelRef.current?.clientHeight);
    }, []);

    return (
        <div className="w-full flex-grow grid grid-cols-12 col-span-full mb-4 gap-x-2" ref={panelRef}>
            <div
                className="col-span-full lg:col-span-6 flex flex-col justify-between items-start lg:border lg:border-neutral-300 lg:rounded-lg lg:shadow-md p-2 lg:overflow-auto"
                style={{ height: panelHeight }}
            >
                <div className="w-full">
                    <p className="w-full h-8 pl-1 text-neutral-500 font-semibold text-lg text-neutral-800">
                        {_t.edit.updateItinerary.incoming()}
                    </p>
                    <div className="w-full pt-1">
                        {incomingDays?.map((day, dayIndex) => {
                            return (
                                <DiffOverviewCell
                                    {...day}
                                    key={day.id}
                                    selfId={day.id}
                                    hasChild={true}
                                    dayIndex={dayIndex}
                                >
                                    <ol className="w-full isolate">
                                        {day.plans.map((plan, planIndex) => {
                                            const incoming = !updatedPlans?.some(
                                                (el) => el?.importId === plan?.importId
                                            );
                                            const diffExist = incoming
                                                ? false
                                                : !updatedDays[dayIndex]?.plans?.some(
                                                      (currPlan) => currPlan?.importId === plan?.importId
                                                  );

                                            return (
                                                <DiffPlanRow
                                                    key={plan?.id || plan?.importId}
                                                    selfId={plan?.id || plan?.importId}
                                                    parentId={day.id}
                                                    importId={plan?.importId}
                                                    incoming={incoming}
                                                    wrongDay={diffExist}
                                                    addIncomingPlan={() => addIncomingPlan(day.id, plan)}
                                                    removePlan={() => removePlan(day.id, plan)}
                                                    hoveredPlanImportId={hoveredPlanImportId}
                                                    handleHoveredPlan={handleHoveredPlan}
                                                    reorderPlan={() => reorderPlan([dayIndex, planIndex])}
                                                    updatedPlaces={updatedPlaces}
                                                    incomingPlaces={incomingPlaces}
                                                    {...plan}
                                                />
                                            );
                                        })}
                                    </ol>
                                </DiffOverviewCell>
                            );
                        })}
                    </div>
                </div>

                <div className="px-2 lg:px-0 py-2">
                    <Button className={addClasses('mt-2')} size="sm" onClick={updateAllPlans}>
                        {_t.edit.updateItinerary.updateAll()}
                    </Button>
                </div>
            </div>
            <div
                className="col-span-full flex flex-col justify-between items-start bg-neutral-100 lg:bg-white lg:col-span-6 lg:border lg:border-neutral-300 rounded-lg lg:shadow-md p-2 lg:overflow-auto"
                style={{ height: panelHeight }}
            >
                <div className="w-full">
                    <p className="w-full h-8 pl-1 text-neutral-500 font-semibold text-lg text-neutral-800">
                        {_t.edit.updateItinerary.updated()}
                    </p>
                    <div className="w-full pt-1">
                        {updatedDays?.map((day, dayIndex) => {
                            return (
                                <DiffOverviewCell
                                    {...day}
                                    key={day.id}
                                    selfId={day.id}
                                    hasChild={true}
                                    dayIndex={dayIndex}
                                >
                                    <ol className="w-full isolate">
                                        {day.plans.map((plan, planIndex) => {
                                            // Segnala se il plan in arrivo e il plan aggiornato si trovano nello stesso giorno
                                            const wrongDay = !incomingDays[dayIndex]?.plans?.some(
                                                (incPlan) => incPlan?.importId === plan?.importId
                                            );

                                            // Segnala se il plan è stato eliminato nell'itinerario originale
                                            const deleted =
                                                plan.importId &&
                                                !incomingPlans.some((incoming) => incoming.importId === plan.importId);

                                            // Recupera il current plan
                                            const currentPlan = currentPlans?.find(
                                                (el) => el?.importId === plan?.importId
                                            );

                                            // Segnala se il plan è stato aggiunto o si trova in un giorno diverso rispetto a quello di origine
                                            const updated =
                                                !currentDays[dayIndex]?.plans?.some(
                                                    (currPlan) => currPlan?.importId === plan?.importId
                                                ) && currentPlan;

                                            return (
                                                <DiffPlanRow
                                                    key={plan?.id}
                                                    selfId={plan?.id}
                                                    parentId={day.id}
                                                    importId={plan?.importId}
                                                    wrongDay={wrongDay}
                                                    deleted={deleted}
                                                    current={true}
                                                    updated={updated}
                                                    hoveredPlanImportId={hoveredPlanImportId}
                                                    handleHoveredPlan={handleHoveredPlan}
                                                    restoreCurrent={() =>
                                                        restorePlan(
                                                            [dayIndex, planIndex],
                                                            [currentPlan?.dayIndex, currentPlan?.planIndex]
                                                        )
                                                    }
                                                    updatedPlaces={updatedPlaces}
                                                    incomingPlaces={incomingPlaces}
                                                    removePlan={() => removePlan(day.id, plan)}
                                                    {...plan}
                                                />
                                            );
                                        })}
                                    </ol>
                                </DiffOverviewCell>
                            );
                        })}
                    </div>
                </div>

                <div className="px-2 lg:px-0 py-2">
                    <Button className={addClasses('mt-2')} size="sm" onClick={restoreAllPlans}>
                        {_t.edit.updateItinerary.restoreAll()}
                    </Button>
                </div>
            </div>
        </div>
    );
}
